import { h, Component } from 'preact'
import qs from 'qs'
import axios, {AxiosError, AxiosResponse} from 'axios'
import globals from '../../utils/globals'
import Header from './Header'
import Loader from '../ui-components/Loader'
import OverviewFilters from './filters/OverviewFilters'
import PaginationComponent from '../ui-components/PaginationComponent'
import Table from '../table/Table'
import EmptyPage from '../empty-page/EmptyPage'
import SaveCustomViewModal from '../moderation-overview/SaveCustomViewModal'
import AlertComponent from '../ui-components/AlertComponent'
import copyToClipboard from '../utils/copyToClipboard'
import tracker from '../../components/tracker/publish'

export type Filters = {
  [filter: string]: Array<any> | string | any
}

export type FilterSelector = {
  id: string,
  type: string,
  name?: string,
  component?: Function,
  items?: Array<any>,
  isSearchable?: boolean,
  fetchUrl?: string,
  isMain?: boolean,
}

type Sort = {
  [key: string]: string,
}

type Props = {
  type: string,
  withFilters?: Array<FilterSelector>,
  withSearch?: boolean,
  withSorting?: Sort,
  withPagination?: {
    page: number,
    pageSize: number
  },
  counterLabel: string,
  rowId: string,
  endpoint: string,
  dataKey: string,
  model: {},
  EmptyStateComponent: JSX.Element
}

type State = {
  data: [],
  total?: string,
  loading: boolean,
  areFiltersApplied: boolean,
  filters?: Filters,
  pagination?: {
    page: number,
    pageSize: number
  },
  sort?: Sort,
  [key: string]: any,
  showSaveCustomViewModal: boolean,
  customViewInputNameModal: string,
  customViewInputNameTitle: string,
  customViewInputNameError: string,
  showAlert: boolean,
  alertMessage: string,
  alertType: string|null,
  alertVisibleFor: number|null,
  customViewId?: number,
  customViewRadioInput: string
}

const createNewCustomView = 'Create new custom view'
const updateExistingCustomView = 'Save changes to the current view'

class Overview extends Component<Props, State> {
  private STORAGE_KEY = `${this.props.type}-filter-criteria`
  private defaultCdn = globals('defaultCdn')

  constructor (props: Props) {
    super(props)
    this.state = {
      data: [],
      total: undefined,
      loading: true,
      areFiltersApplied: false,
      ...this.getDefaultFilteringOptions(),
      showSaveCustomViewModal: false,
      customViewInputNameModal: '',
      customViewInputNameTitle: '',
      customViewInputNameError: '',
      showAlert: false,
      alertMessage: '',
      alertType: null,
      alertVisibleFor: null,
      customViewRadioInput: createNewCustomView
    }

    this.handleResponse = this.handleResponse.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.onClearFilterCriteria = this.onClearFilterCriteria.bind(this)
    this.onShareView = this.onShareView.bind(this)
    this.onSortChange = this.onSortChange.bind(this)
    this.onSaveCustomView = this.onSaveCustomView.bind(this)
    this.onRefresh = this.onRefresh.bind(this)
    this.handleCustomViewCreateError = this.handleCustomViewCreateError.bind(this)
    this.handleCustomViewCreateSuccess = this.handleCustomViewCreateSuccess.bind(this)
    this.handleCustomViewUpdateSuccess = this.handleCustomViewUpdateSuccess.bind(this)
  }

  getDefaultFilteringOptions () : { [field: string] : {}} {
    const { withFilters, withSearch, withSorting, withPagination } = this.props
    const filteringOptions = {}

    if (withFilters) {
      const filterSelectors = withFilters
      const emptyFilters = {}
      filterSelectors.forEach((selector: FilterSelector) => {
        if(selector.id === 'separator') return
        const payload = selector.type === 'radio' ? null : []
        Object.assign(emptyFilters, { [selector.id]: payload })
      })

      if(withSearch) {
        Object.assign(emptyFilters, { search: '' })
      }

      Object.assign(filteringOptions, { filters: emptyFilters })
    }

    if (withSorting) {
      const sort = {}
      Object.keys(withSorting).forEach((sorting: string) => {
        Object.assign(sort, { [sorting]: withSorting[sorting] })
      })
      Object.assign(filteringOptions, { sort: sort })
    }

    if (withPagination) {
      const { page, pageSize } = withPagination
      Object.assign(filteringOptions, { pagination: { page, pageSize } })
    }

    return filteringOptions
  }

  applyFiltersFromStorage () {
    if (!window.sessionStorage.getItem(this.STORAGE_KEY)) {
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
    } else {
      const filterCriteria = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      const areFiltersApplied = JSON.stringify(filterCriteria.filters) !== JSON.stringify(this.getDefaultFilteringOptions().filters)
      this.setState({ ...filterCriteria, ...{ areFiltersApplied } })
    }
  }

  static getCustomViewId () {
    return parseInt(window.location.pathname.split('/overview/')[1])
  }

  static getSharedView () {
    const search = qs.parse(window.location.search, { ignoreQueryPrefix: true })
    const view = search?.view

    try {
      return JSON.parse(window.atob(view))
    } catch (e) {
      return null
    }
  }

  static isCustomView (): boolean {
    return !!Overview.getCustomViewId()
  }

  static isSharedView (): boolean {
    return !!Overview.getSharedView()
  }

  async getView () {
    const customViewId = Overview.getCustomViewId()
    if (customViewId) {
      await this.fetchCustomView(customViewId)
      return
    }

    const sharedView = Overview.getSharedView()
    if (sharedView) {
      this.setSharedView(sharedView)
      return
    }

    this.applyFiltersFromStorage()
  }

  async componentDidMount () {
    await this.getView()
    await this.fetch()
  }

  async fetchCustomView (viewId: number) {
    await axios.get(`/view/${viewId}`)
      .then(res => this.setCustomView(res, viewId))
      .catch(() => {})
  }

  async fetch () {
    this.setState({ loading: true })
    const { endpoint } = this.props
    const { pagination, sort, filters } = this.state
    await axios.post(endpoint, {
      pagination,
      sort,
      filters: {
        ...filters,
        ...{
          assignedModerator: filters?.assignedModerator ? filters?.assignedModerator.map((filter: { value: string }) => filter.value) : [],
        }
      }
    })
      .then(res => this.handleResponse(res))
      .catch(() => this.setState({ loading: false }))
  }

  async createCustomView () {
    const { customViewInputNameModal, filters, sort } = this.state
    const data = {
      name: customViewInputNameModal,
      criteria: {
        filters: filters,
        sort: sort
      }
    }

    await axios.put('/view', data)
      .then(this.handleCustomViewCreateSuccess)
      .catch(this.handleCustomViewCreateError)
  }

  async updateCustomView () {
    const { customViewId, customViewInputNameTitle, filters, sort } = this.state
    const data = {
      name: customViewInputNameTitle,
      criteria: {
        filters: filters,
        sort: sort
      }
    }

    await axios.post(`/view/${customViewId}`, data)
      .then(this.handleCustomViewUpdateSuccess)
      .catch(() => this.showSomethingWentWrong())
  }

  onPageChange (page: number) {
    const pageSize = this.state.pagination?.pageSize
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onPageSizeChange (pageSize: number) {
    const page = this.props.withPagination?.page
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onSortChange (order: string) {
    const direction = this.state.sort?.[order]

    if (direction === 'desc') this.updateFilterCriteria('sort', {...this.state.sort, ...{ [order]:'asc' }})
    if (direction === 'asc') this.updateFilterCriteria('sort', {...this.state.sort, ...{ [order]:'desc' }})
  }

  onFilterChange (filter: string, payload: Array<string> | string | null) {
    const { filters } = this.state
    this.updateFilterCriteria('filters', {...filters, ...{ [filter]: payload }})
    // always to to the first page when updating filters
    const pagination = { page: 1, pageSize: this.state.pagination!.pageSize }
    this.setState({ areFiltersApplied: true, pagination })
  }

  onClearFilterCriteria () {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, () => {
        this.fetch()
      })
    } else {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, () => {
        this.fetch().then(() => {
          window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
        })
      })
    }
  }
  onRefresh() {
    this.onPageChange(1)
  }

  onShareView () {
    const sharableViewUrl = this.getSharableViewUrl()

    copyToClipboard(sharableViewUrl)

    this.setState({
      showAlert: true,
      alertMessage: 'Link copied to clipboard!',
      alertType: 'success',
      alertVisibleFor: 3000
    })
  }

  async onSaveCustomView () {
    if (this.isCreateCustomView()) {
      this.validateCustomViewModalName()
      if (this.state.customViewInputNameError) {
        return
      }
      await this.createCustomView()
    } else {
      this.validateCustomViewTitleName()
      if (this.state.alertType === 'error') {
        return
      }
      await this.updateCustomView()
    }
  }

  updateFilterCriteria (key: string, value: {}) {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ [key]: value }, () => {
        this.fetch()
      })
    } else {
      let storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      storage[key] = value
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
      this.setState({ [key]: storage[key] }, () => {
        this.fetch()
      })
    }
  }

  handleResponse (response: AxiosResponse) {
    const { total } = response.data
    const data = response.data[this.props.dataKey]

    this.setState({
      data: data || [],
      total: total || undefined,
      loading: false
    })
  }

  handleCustomViewCreateSuccess (response: AxiosResponse) {
    const { data } = response

    if (data === undefined) {
      return
    }

    this.redirectToCustomView(data.viewId)
  }

  handleCustomViewUpdateSuccess (response: AxiosResponse) {
    this.redirectToCustomView(this.state.customViewId as number)
  }

  redirectToCustomView (viewId: number) {
    window.location.assign(`/overview/${viewId}?showMessage=success`)
  }

  handleCustomViewCreateError (error: AxiosError) {
    if (error.response && error.response.status === 422) {
      this.setState({ customViewInputNameError: 'This custom view name already exists' })
      return
    }

    this.showSomethingWentWrong()
  }

  showSomethingWentWrong () {
    this.setState({
      showAlert: true,
      alertMessage: 'Something went wrong. Please try again',
      alertType: 'error'
    })
  }

  setCustomView (response: AxiosResponse, customViewId: number) {
    if (response.data.length === 0) {
      return
    }

    const { name, criteria } = response.data
    const { filters, sort } = criteria

    this.setState({
      customViewId,
      filters,
      sort,
      areFiltersApplied: true,
      customViewRadioInput: updateExistingCustomView,
      customViewInputNameTitle: name
    })

    if (window.location.search.includes('?showMessage=success')) {
      this.setState({
        showAlert: true,
        alertMessage: `Custom view ‘${name}’ saved successfully!`,
        alertType: 'success',
        alertVisibleFor: 3000
      })
      window.history.replaceState(null, '', customViewId.toString())
    }

    this.trackCustomViewOpened()
  }

  setSharedView (sharedView: {filters: Filters, sort: Sort}) {
    const { filters, sort } = sharedView

    this.setState({
      filters: filters,
      sort: sort
    })
  }

  getSharableViewUrl (): string {
    const { filters, sort } = this.state

    const view = window.btoa(JSON.stringify({
      filters: filters,
      sort: sort
    }))

    return `${window.location.origin}/overview?view=${view}`
  }

  getPaginationProps () {
    const { withPagination } = this.props
    if(!withPagination) return

    const { pagination, total } = this.state

    return {
      pagination: Object.assign({},  pagination, { total }),
      onPageChange: this.onPageChange,
      onPageSizeChange: this.onPageSizeChange
    }
  }

  getTableProps () {
    const { withSorting, model, rowId } = this.props
    const { data } = this.state

    const tableProps = { model, rows: data, rowId}

    if (withSorting) {
      const { sort } = this.state
      sort && Object.keys(sort).forEach((key: string) => {
        Object.assign(tableProps, { sort: { order: key, direction: sort[key] }, onSortChange: this.onSortChange })
      })
    }

    return tableProps
  }

  isCreateCustomView () {
    return this.state.customViewRadioInput === createNewCustomView
  }

  validateCustomViewModalName () {
    const { customViewInputNameModal } = this.state

    if (!customViewInputNameModal) {
      this.setState({ customViewInputNameError:
            'Please enter a name for your custom view' })
      return
    }

    if (customViewInputNameModal.length > 250) {
      this.setState({ customViewInputNameError:
            'Maximum length: 250 characters. Please reduce the length of the name of your custom view' })
      return
    }

    this.setState({ customViewInputNameError: '' })
  }

  validateCustomViewTitleName () {
    const { customViewInputNameTitle } = this.state

    if (!customViewInputNameTitle) {
      this.setState({
        showAlert: true,
        alertType: 'error',
        alertMessage: 'Please enter a name for your custom view',
        showSaveCustomViewModal: false
      })
      return
    }
  }

  trackCustomViewOpened () {
    tracker.trackEvent('userevent', {
      type: 'Custom View Opened',
      new_overview: true
    })
  }

  render ({
    type,
    withSearch,
    withPagination,
    withFilters,
    counterLabel,
    rowId,
    model,
    EmptyStateComponent
  }: Props, {
    data,
    total,
    filters,
    loading,
    areFiltersApplied,
    sort,
    showSaveCustomViewModal,
    customViewInputNameModal,
    customViewInputNameError,
    customViewInputNameTitle,
    showAlert,
    alertMessage,
    alertType,
    alertVisibleFor,
    customViewId,
    customViewRadioInput
  }: State) {

    return <div class='overview'>
      {loading && <Loader />}
      {showAlert && <AlertComponent
        message={alertMessage}
        messageType={alertType}
        isVisibleFor={alertVisibleFor}
        sticky
        onTimeoutEnd={() => this.setState({ showAlert: false })}
      />}
      <Header type={type}
        title={ !!customViewId ? customViewInputNameTitle : (type === 'ideation'? 'Ideas' : 'Overview')}
        isCustomView={!!customViewId}
        onTitleChanged={(title: string) => this.setState({ customViewInputNameTitle: title})}
        onSaveViewClicked={() => this.setState({ showSaveCustomViewModal: true })}
        onShareView={this.onShareView}
      />
      { withFilters && (data.length > 0 || areFiltersApplied || Overview.isSharedView()) && <OverviewFilters
        filters={withFilters}
        appliedFilters={filters}
        onFilterChange={this.onFilterChange}
        onClearFilterCriteria={this.onClearFilterCriteria}
        withSearch={withSearch}
        onRefresh={this.onRefresh}
      /> }
      {data.length > 0
        ? <div>
          { withPagination && <div class='overview__table-info'>
            <span class='overview__counter'>{counterLabel} ({total})</span>
            <PaginationComponent {...this.getPaginationProps()} />
          </div> }
          <Table {...this.getTableProps()} />
          { withPagination && <PaginationComponent {...this.getPaginationProps()} displayListUpwards /> }
        </div>
        : (!loading && (areFiltersApplied || Overview.isSharedView()))
          ? <div>
            <EmptyPage
              img_src={`${this.defaultCdn}/images/empty_result.png`}
              header='No results found'
              text={'Sorry, that filter combination has no results.\n Please try different criteria.'}
            />
          </div> : !loading && EmptyStateComponent
      }
      <SaveCustomViewModal
        customViewName={customViewInputNameModal}
        customViewRadioInput={customViewRadioInput}
        isCustomView={!!customViewId}
        onClose={() => this.setState({ showSaveCustomViewModal: false })}
        onCustomViewNameChanged={(e: ChangeEvent) => this.setState({ customViewInputNameModal: e.target.value })}
        onRadioButtonChanged={(option) => this.setState({ customViewRadioInput: option })}
        onSaveCustomView={this.onSaveCustomView}
        radioOptions={[updateExistingCustomView, createNewCustomView]}
        showModal={showSaveCustomViewModal}
        errorMessage={customViewInputNameError}
      />
    </div>
  }
}

export default Overview
